import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ck_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => {
        const byId = Object.fromEntries(data.map((p) => [p.id, p]));
        const valid = (i) => {
          const p = byId[i.product_id];
          const names = (p?.colours || []).map((c) => c.name);
          return p && (!names.length || names.includes(i.colour));
        };
        setItems((prev) => {
          const kept = prev.filter(valid);
          if (kept.length < prev.length)
            toast.info("Some items in your bag are no longer available and were removed", { id: "cart-pruned" });
          return kept.length < prev.length ? kept : prev;
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("ck_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, size, colour, quantity = 1, image) => {
    setItems((prev) => {
      const key = `${product.id}-${colour || ""}-${size}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          size,
          colour,
          price: product.price,
          image: image || product.images?.[0],
          quantity,
        },
      ];
    });
    toast.success(`${product.name} (${[colour, size].filter(Boolean).join(", ")}) added to bag`);
    setOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const updateQty = (key, quantity) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, quantity) } : i)));
  const clear = () => setItems([]);

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, total, count, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
